import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { showNotification } from 'react-admin';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import { Box, CircularProgress } from '@material-ui/core';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getAllTags, updateFileSystemNodeTags } from '../../../../../services/file-explorer/fileExplorerApi';
import { useQuery, useQueryClient } from 'react-query';
import GridTagFields from '../../../tags/GridTagFields';
import { getTagFormDependencies } from '../../../../../services/file-explorer/tags/tagFormInputRepository';
import CenteredSpinner from '../../../../loading/CenteredSpinner';
import { getResourceByPath } from '../../../../../services/resource/resourceApi';

function EditFileSystemNodeTagsDialog(props) {
    const { open, fileSystemNodePath, handleCancel, showNotification } = props;

    const translate = useTranslate();
    const queryClient = useQueryClient();

    const [isLoading, setIsLoading] = useState(false);

    const slashConcatenatedFileSystemNodePath = fileSystemNodePath.join('/');
    const { data: fileSystemNodeDescriptor, isLoading: isFileSystemNodeLoading } = useQuery(['/resource/{path}', 'getResourceByPath', slashConcatenatedFileSystemNodePath], () => getResourceByPath(slashConcatenatedFileSystemNodePath));
    
    const { data: tagGroups } = useQuery('/file-explorer/tag', getAllTags);
    const { mandatoryTagGroups, optionalTags, fields, defaultValues } = getTagFormDependencies(tagGroups, translate, fileSystemNodeDescriptor?.tags ?? []);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(yup.object().shape(fields)),
        defaultValues: defaultValues,
        criteriaMode: 'all',
    });

    
    useEffect(() => {
        if(!isFileSystemNodeLoading) {
            resetDialog();
        }
    }, [isFileSystemNodeLoading]); 

    const startstartSavingTags = useCallback((data) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        const cancellationTokenSource = axios.CancelToken.source();
        const tags = [];
        for (let tagsField of Object.values(data)) {
            for (let tag of tagsField) {
                tags.push(tag.id);
            }
        }

        updateFileSystemNodeTags(fileSystemNodePath, tags, cancellationTokenSource.token)
        .then(() => {
            handleCancelInternal();
            queryClient.invalidateQueries(['/resource/{path}', 'getResourceByPath', slashConcatenatedFileSystemNodePath]);
            queryClient.invalidateQueries(['/file-explorer/search', 'getDirectoryContent']);
            setIsLoading(false);
            showNotification(`cmss.resources.file-explorer.directory-actions-menu.edit-file-system-node-tags.update-successful`, 'success');
        }).catch((error) => {
            showNotification(`cmss.resources.file-explorer.directory-actions-menu.edit-file-system-node-tags.update-unsuccessful`, 'warning');
            setIsLoading(false);
            if (axios.isCancel(error)) {
                return;
            } 
        });
    
        return () => {
            cancellationTokenSource.cancel();
        }
    }, [isLoading, fileSystemNodePath, queryClient]);

    function resetDialog() {
        reset(defaultValues);
        setIsLoading(false);
    }

    function handleOnClickSaveButton(data) {
        startstartSavingTags(data);
    }

    function handleCancelInternal() {
        handleCancel();
    }

    return (
        <Dialog open={open} TransitionProps={{onEnter: resetDialog, onClose: handleCancelInternal}} fullWidth={true} maxWidth="sm">
            <DialogTitle>{translate(`cmss.resources.file-explorer.directory-actions-menu.buttons.edit-tags`)}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {
                        !isFileSystemNodeLoading &&
                        <GridTagFields control={control} errors={errors} mandatoryTagGroups={mandatoryTagGroups} optionalTags={optionalTags}/>
                    }
                    {
                        isFileSystemNodeLoading &&
                        <Grid item xs={12}>
                            <Box m={2}>
                                <CenteredSpinner/>
                            </Box>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                variant="contained"
                disabled={isLoading || isFileSystemNodeLoading}
                onClick={handleSubmit(handleOnClickSaveButton)}
                color="primary"
                >
                    {translate(`cmss.resources.file-explorer.directory-actions-menu.edit-file-system-node-tags.save`)}
                    {isLoading && (
                        <CircularProgress
                            size={24}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Button>
                <Button onClick={handleCancelInternal} color="secondary">
                    {translate(`cmss.resources.file-explorer.directory-actions-menu.edit-file-system-node-tags.cancel`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default connect(null, {
    showNotification
})(EditFileSystemNodeTagsDialog);